import { LoaderCircle } from 'lucide-react'
import './Button.css'

/**
 * @param {'primary'|'danger'|'success'|'warning'|'ghost'|'soft'|'quiet'} variant
 * @param {'sm'|'md'|'lg'} [size]
 * @param {boolean} [icon]   icon-only — square, and the accessible name has to
 *                           come from `aria-label` since there is no text
 * @param {boolean} [block]  fill the inline axis
 * @param {boolean} [busy]   THE ACTION IS RUNNING. A spinner takes the glyph's place and
 *                           the button stops accepting clicks, while the label stays put
 *                           so nothing under the cursor moves.
 *
 *   This is where an upload's waiting belongs. It used to be announced by a progress bar
 *   on the file field, which started when a file was PICKED — before anything was sent —
 *   and finished before the save began. The wait is here, between pressing save and the
 *   answer, and it is the button that should say so.
 *
 * @param {import('react').ElementType} [as]  render as a link (`as={Link}`) when
 *                                            the button navigates rather than acts
 */
export default function Button({
  variant = 'primary',
  size = 'md',
  icon = false,
  block = false,
  busy = false,
  as: Tag = 'button',
  type = 'button',
  disabled = false,
  className = '',
  children,
  ...rest
}) {
  const classes = [
    'btn',
    `btn--${variant}`,
    size !== 'md' && `btn--${size}`,
    icon && 'btn--icon',
    block && 'btn--block',
    busy && 'btn--busy',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Tag
      className={classes}
      {...(Tag === 'button' ? { type, disabled: disabled || busy } : null)}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy && <LoaderCircle className="btn__spin" size={16} strokeWidth={2} aria-hidden="true" />}
      {icon ? children : isolate(children)}
    </Tag>
  )
}

/**
 * Isolate the TEXT of a button, not its whole content.
 *
 * Wrapping everything in one <bdi> put the glyph and the label inside a single
 * inline box, so the flex `gap` never applied between them and the glyph was
 * placed by bidi resolution rather than by the layout — which is why icons sat
 * flush against their labels and, on a mixed Arabic/Latin label, on the wrong
 * side of them.
 *
 * Isolating each text run instead makes the glyph and the label real flex
 * items: the gap applies, and the row direction puts the glyph at the
 * reading-start for free.
 */
function isolate(children) {
  if (typeof children === 'string' || typeof children === 'number') return <bdi>{children}</bdi>
  if (!Array.isArray(children)) return children
  return children.map((child, index) =>
    typeof child === 'string' || typeof child === 'number' ? (
      // eslint-disable-next-line react/no-array-index-key -- children are static per call site
      <bdi key={index}>{child}</bdi>
    ) : (
      child
    ),
  )
}
